import React, { useCallback, useState } from 'react'
import { useNavigate, generatePath } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Alert, Stack } from '@mui/material'
import { errorText, secondaryText } from '@/constant'
import { PlainCard, Row } from './components'
import Text from '@/components/Text'
import { ConfirmButton } from '@/components/Button'
import { ColumnAction, pageProps } from '@/components/Table'
import { Columns } from './column'
import Table from '@/components/Table'
import { Message } from '@cx/ui'
import Modal, { confirm } from '@/components/Modal'
import { ForwardingConfigDetails } from '@/types'
import { getConfigList, deleteConfig, testMessage, enableConfig } from '@/api'
import InfoIcon from '@mui/icons-material/Info'
import wechat from '@/asset/img/wechat_service.webp'
import { ImageIcon } from '@/icon'
import { ClearOutlined } from '@mui/icons-material'

const Forwarding: React.FC<unknown> = () => {
  const navigate = useNavigate()
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [rows, setRows] = useState<number>(10)

  const [showCode, setShowCode] = useState<boolean>(false)

  const { data, refetch: refetchList } = useQuery(
    ['getConfigList', page, rows],
    () => getConfigList({ page, size: rows }),
    {
      onSuccess: (res) => {
        setTotal(res?.total)
      },
    },
  )

  const { mutate: deleteMutate } = useMutation(deleteConfig, {
    onSuccess: () => {
      Message.success('删除配置成功')
      refetchList()
    },
  })

  const { mutate: testConfigMutation } = useMutation(testMessage, {
    onSuccess: (res) => {
      if (res?.addresses?.length > 0) {
        Message.error('发信失败')
      } else {
        Message.success('消息已发送')
      }
    },
  })

  const { mutate: updateConfigEnabled } = useMutation(enableConfig, {
    onSuccess: () => {
      refetchList()
    },
    onError: (err: string) => {
      if (err === '需要认证百川公众号才能开启') {
        setShowCode(true)
      }
    },
  })

  const handleCloseCode = useCallback(() => {
    setShowCode(false)
  }, [])

  const handlePageChange = (value: pageProps) => {
    setPage(value.page)
    // @ts-ignore
    setRows(value.rowsPerPage)
  }

  const toCreate = useCallback(() => {
    navigate('/console/account/notifications/config/createConfig')
  }, [navigate])

  const toEdit = useCallback(
    (id: number) => {
      const path = generatePath('/console/account/notifications/config/:id', {
        id: id.toString(),
      })
      navigate(path)
    },
    [navigate],
  )

  const toDelete = useCallback(
    (id: number) => {
      confirm({
        onConfirm: () => {
          deleteMutate(id)
        },
        // @ts-ignore
        onClose: null,
        title: '提示',
        content: '确认删除此配置',
        closeAfterClose: true,
      })
    },
    [deleteMutate],
  )

  const updateStatus = useCallback(
    (data: ForwardingConfigDetails) => {
      updateConfigEnabled({
        // @ts-ignore
        id: data?.id,
        enabled: data.enabled === 1 ? 0 : 1,
      })
    },
    [updateConfigEnabled],
  )

  const testConfig = useCallback(
    (data: ForwardingConfigDetails) => {
      testConfigMutation({ id: data?.id })
      {
      }
    },
    [testConfigMutation],
  )

  const columnsAction = useCallback(
    (action: ColumnAction, data: ForwardingConfigDetails) => () => {
      switch (action) {
        case 'edit': {
          // @ts-ignore
          toEdit(data?.id)
          break
        }
        case 'delete': {
          // @ts-ignore
          toDelete(data?.id)
          break
        }
        case 'status': {
          updateStatus(data)
          break
        }
        case 'test': {
          testConfig(data)
          break
        }
      }
    },
    [testConfig, toDelete, toEdit, updateStatus],
  )

  return (
    <PlainCard>
      <Row sx={{ justifyContent: 'space-between', zIndex: 3 }}>
        <Alert
          severity='info'
          sx={{ alignItems: 'center' }}
          icon={
            <InfoIcon
              sx={{
                color: '#2f7ce9',
                fontSize: '14px',
              }}
            />
          }
        >
          <Text size='xs' sx={{ color: secondaryText }}>
            提醒：系统默认发送消息到百川站内，此处设置邮箱、钉钉、飞书、企业微信等以第一时间接收告警等消息。
          </Text>
        </Alert>
        <ConfirmButton onClick={toCreate}>新建通知策略</ConfirmButton>
      </Row>
      <Table<ForwardingConfigDetails & { action?: unknown }>
        rowKey='id'
        columns={Columns(columnsAction)}
        data={data?.detail ?? []}
        pagination={{
          page,
          total,
          onChange: handlePageChange,
          rowsPerPage: rows,
        }}
      />
      <Modal open={showCode} onClose={handleCloseCode} width={'300px'}>
        <PlainCard>
          <Row sx={{ justifyContent: 'space-between' }}>
            <Text weight='bold'>请先关注百川服务号</Text>
            <ClearOutlined sx={{ cursor: 'pointer' }} onClick={handleCloseCode} />
          </Row>
          <Stack
            sx={{
              alignItems: 'center',
            }}
          >
            <ImageIcon src={wechat} sx={{ width: '200px' }} />
            <Text size='xs' sx={{ color: secondaryText, margin: '8px' }}>
              长亭百川云
            </Text>
            <Text size='xs' sx={{ color: errorText, margin: '8px', fontSize: '12px' }}>
              该账号与订阅号不一致，必须单独关注才可接收告警消息
            </Text>
          </Stack>
        </PlainCard>
      </Modal>
    </PlainCard>
  )
}

export default Forwarding
